"use client";
import React, { useState, useEffect, useRef } from "react";
import companyIcon from "@/assets/svgs/company.svg";
import Image from "next/image";
import Button from "@/common/Button";
import useBackendUrl from "@/hooks/useBackendUrl";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation";
import checkIcon from "@/assets/svgs/check.svg";
import preferencesIcon from "@/assets/svgs/preferences.svg";
import Link from "next/link";
import {
  companyCountries,
  carBrands,
  minBuyPriceValues,
  maxBuyPriceValues,
  buyAgeMinValues,
  buyAgeMaxValues,
  buyKmMinValues,
  buyKmMaxValues,
} from "./selectOptions";
import { useAuth } from "@/context/AuthProvider";
import { showToast } from "@/app/helpers/showToast";
import { useAsyncRoutePush } from "@/hooks/useAsyncPush";
import ErrorTooltip from "@/components/ErrorTooltip/ErrorTooltip";

function RegistrierenMain() {
  // Step 1 fields
  const [vorname, setVorname] = useState("");
  const [nachname, setNachname] = useState("");
  const [telefon, setTelefon] = useState("");
  const [averageCarsPerMonth, setAverageCarsPerMonth] =
    useState("Keine Angabe");
  const [email, setEmail] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const asyncPush = useAsyncRoutePush();

  // Step 2 fields
  const [companyCountry, setCompanyCountry] = useState("");
  const [companyStreet, setCompanyStreet] = useState("");
  const [companyAdditionalAddress, setCompanyAdditionalAddress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [companyState, setCompanyState] = useState("");
  const [website, setWebsite] = useState("");
  const [firmenname, setFirmenname] = useState("");
  const [password, setPassword] = useState("");

  // Step 3 (Preferences) fields
  const [vehicleBrand, setVehicleBrand] = useState("all");
  const [selectedBrands, setSelectedBrands] = useState([]);

  const [buyPriceMin, setBuyPriceMin] = useState("");
  const [buyPriceMax, setBuyPriceMax] = useState("");
  const [buyAgeMin, setBuyAgeMin] = useState("");
  const [buyAgeMax, setBuyAgeMax] = useState("");
  const [buyKmMin, setBuyKmMin] = useState("");
  const [buyKmMax, setBuyKmMax] = useState("");

  const [isVehicleDropdownOpen, setIsVehicleDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Disable buttons for each step
  const [stepOneDisabled, setStepOneDisabled] = useState(false);
  const [stepTwoDisabled, setStepTwoDisabled] = useState(false);
  const [stepThreeDisabled, setStepThreeDisabled] = useState(false);

  const { login } = useAuth();
  const api = useBackendUrl();

  // State to track blur for inputs (for styling purposes)
  const [isBlurred, setIsBlurred] = useState({
    vorname: false,
    nachname: false,
    telefon: false,
    email: false,
    password: false,
    firmenname: false,
    companyCountry: false,
    companyStreet: false,
    postalCode: false,
    city: false,
    companyState: false,
  });

  // Common handlers
  const handleChange = (setter) => (e) => setter(e.target.value);
  const handleDropdownChange = (setter) => (e) => setter(e.target.value);

  // Converts values to numbers if not special strings
  const convertValue = (value) => {
    if (value === "Unbegrenzt" || value === "Älter als 30") {
      return value;
    }
    return Number(value);
  };

  // --- UPDATED getInputClassName ---
  const getInputClassName = (field) => {
    /* ---------- 2. Regex‑Validierung ---------- */
    if (field === "email") {
      const ok = /^[\w.%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email);
      return ok ? "input-valid" : isBlurred.email ? "input-error" : "";
    }

    if (field === "telefon") {
      const ok = /^(?:\+43|0)\d{9,11}$/.test(telefon);
      return ok ? "input-valid" : isBlurred.telefon ? "input-error" : "";
    }

    if (field === "password") {
      /* Zeige erst nach Blur rot an */
      if (!isBlurred.password) return "";
      const strong =
        password.length >= 7 &&
        /[A-Za-z]/.test(password) &&
        /[0-9]/.test(password);
      return strong ? "input-valid" : "input-error";
    }

    /* ---------- 3. Einfache Pflichtfelder ---------- */
    const simpleMap = {
      vorname,
      nachname,
      firmenname,
      companyCountry,
      companyStreet,
      postalCode,
      city,
      companyState,
    };

    if (field in simpleMap) {
      return simpleMap[field]
        ? "input-valid"
        : isBlurred[field]
        ? "input-error"
        : "";
    }

    return "";
  };

  // --- END getInputClassName ---

  const isStrongPassword = (pw) =>
    pw.length >= 7 && /[A-Za-z]/.test(pw) && /[0-9]/.test(pw);

  useEffect(() => {
    const isStep1Valid =
      vorname.trim() &&
      nachname.trim() &&
      telefon.trim() &&
      email.trim() &&
      isStrongPassword(password) &&
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/.test(email) &&
      /^(?:\+43|0)\d{1,12}$/.test(telefon);
    setStepOneDisabled(!isStep1Valid);
  }, [vorname, nachname, telefon, email, password]);

  // Validate Step 2 fields
  useEffect(() => {
    const isStep2Valid =
      firmenname.trim() &&
      companyCountry.trim() &&
      companyStreet.trim() &&
      postalCode.trim() &&
      city.trim() &&
      companyState.trim();
    setStepTwoDisabled(!isStep2Valid);
  }, [firmenname, companyCountry, companyStreet, postalCode, city, companyState]);

  useEffect(() => {
    // kein Button‑Unlock, solange nichts gewählt
    const filled = !!averageCarsPerMonth;
    setStepThreeDisabled(!filled);
  }, [averageCarsPerMonth]);

  // Close dropdown if clicking outside
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsVehicleDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle blur events for inputs
  const handleBlur = (field) => {
    setIsBlurred((prev) => ({ ...prev, [field]: true }));
  };

  // 1️⃣  Felder von Schritt 1 markieren, falls sie NOCH ungültig sind
  const markStep1 = () =>
    setIsBlurred((prev) => ({
      ...prev,
      vorname: !vorname.trim(), // leer?
      nachname: !nachname.trim(),
      telefon: !/^(?:\+43|0)\d{9,11}$/.test(telefon), // Regex‑Test
      email: !/^[\w.%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email),
      password: !isStrongPassword(password),
    }));

  // 2️⃣  Felder von Schritt 2 markieren, falls sie NOCH ungültig sind
  const markStep2 = () =>
    setIsBlurred((prev) => ({
      ...prev,
      firmenname: !firmenname.trim(),
      companyCountry: !companyCountry.trim(),
      companyStreet: !companyStreet.trim(),
      postalCode: !postalCode.trim(),
      city: !city.trim(),
      companyState: !companyState.trim(),
    }));

  const stepOneFunction = (e) => {
    e.preventDefault(); // Form‑Submit unterbinden
    if (stepOneDisabled) {
      // noch Fehler → Felder markieren
      markStep1();
      return;
    }
    window.scrollTo?.({ top: 0, behavior: "smooth" });
    setStep(2);
  };

  const stepTwoFunction = (e) => {
    e.preventDefault();
    if (stepTwoDisabled) {
      markStep2();
      return;
    }
    window.scrollTo?.({ top: 0, behavior: "smooth" });
    setStep(3);
  };

  const handleBrandSelection = (option) => {
    setSelectedBrands((prev) =>
      prev.includes(option)
        ? prev.filter((brand) => brand !== option)
        : [...prev, option]
    );
  };

  const handleSubmit = async () => {
    // erst gar nicht absenden, wenn einer der Steps blockiert ist
    if (stepOneDisabled || stepTwoDisabled || stepThreeDisabled) return;

    /* 1️⃣  Payload ‑ alle Felder sind vorhanden, leere → null */
    const payload = {
      vorname,
      nachname,
      telefon,
      email,
      password,
      firmenname,
      addresse: {
        country: companyCountry,
        street: companyStreet,
        postalCode,
        city,
        state: companyState,
        additional: companyAdditionalAddress || null,
      },
      website: website || null,

      preferences: {
        brands:
          vehicleBrand === "specific" && selectedBrands.length
            ? selectedBrands
            : null,
        minBuyPrice: buyPriceMin ? convertValue(buyPriceMin) : null,
        maxBuyPrice: buyPriceMax ? convertValue(buyPriceMax) : null,
        minBuyKm: buyKmMin ? convertValue(buyKmMin) : null,
        maxBuyKm: buyKmMax ? convertValue(buyKmMax) : null,
        minBuyAge: buyAgeMin ? convertValue(buyAgeMin) : null,
        maxBuyAge: buyAgeMax ? convertValue(buyAgeMax) : null,
      },

      averageCarsPerMonth, // bleibt Pflichtfeld
    };

    try {
      setLoading(true);

      /* 2️⃣  Request */
      const response = await fetch(`${api}/auktionRegistierung/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (!response.ok) {
        showToast(
          data.error === "Nutzer mit dieser Email existiert bereits"
            ? "Nutzer mit dieser Email existiert bereits"
            : "Fehler bei der Registrierung",
          "error"
        );
        console.error(data.error);
        return;
      }

      /* 3️⃣  Direkt einloggen, Success‑Flow */
      const loginResult = await login(email, password, false, {
        redirectTo: null,
      });
      if (!loginResult.success) {
        showToast("Login nach Registrierung fehlgeschlagen", "error");
        return;
      }

      showToast("Erfolgreich registiert", "success");
      await asyncPush("/verifikation");
    } catch (err) {
      console.error(err);
      showToast("Ein unerwarteter Fehler ist aufgetreten", "error");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingAnimation style={{ marginTop: "209px" }} />;

  return (
    <div className="RegistrierenMain">
      <div className="RegistrierenMain-wrapper">
        <div className="RegistrierenMain-top">
          <h1>Erstelle dein Konto</h1>
          <p>
            Gib deine Informationen ein, um Autoeinfachlos-Kaufpartner zu
            werden.
          </p>
        </div>

        {step === 1 && (
          <div className="RegistrierenMain-center-wrapper">
            <div className="Register-steps-wrapper">
              <div className="Register-steps-line"></div>
              <div className="Register-step-outer active">
                <span>1</span>
              </div>
              <div className="Register-step-outer">
                <span>2</span>
              </div>
              <div className="Register-step-outer">
                <span>3</span>
              </div>
            </div>
            {/* <div className="Registrieren-main-title">
              <Image
                src={detailsIcon}
                className="Registrieren-main-title-icon"
                alt="Details"
              />
              <h3>Deine Details</h3>
            </div> */}
            <form>
              <div className="RegistrierenMain-inputs-wrapper">
                <div className="input-group">
                  <label htmlFor="vorname">Vorname*</label>

                  <ErrorTooltip
                    message="Bitte gib deinen Vornamen ein"
                    visible={isBlurred.vorname && vorname.length === 0}
                  >
                    <input
                      type="text"
                      id="vorname"
                      placeholder="Max"
                      value={vorname}
                      onChange={handleChange(setVorname)}
                      onBlur={() => handleBlur("vorname")}
                      className={getInputClassName("vorname")}
                    />
                  </ErrorTooltip>
                </div>
                <div className="input-group">
                  <label htmlFor="nachname">Nachname*</label>

                  <ErrorTooltip
                    message="Bitte gib deinen Nachnamen ein"
                    visible={isBlurred.nachname && vorname.length === 0}
                  >
                    <input
                      type="text"
                      id="nachname"
                      placeholder="Mustermann"
                      value={nachname}
                      onChange={handleChange(setNachname)}
                      onBlur={() => handleBlur("nachname")}
                      className={getInputClassName("nachname")}
                    />
                  </ErrorTooltip>
                </div>
                <div className="input-group">
                  <label htmlFor="telefon">Telefonnummer*</label>

                  <ErrorTooltip
                    message="Bitte gib eine gültige Rufnummer ein"
                    visible={
                      isBlurred.telefon && !/^(?:\+43|0)\d{9,11}$/.test(telefon) // Regex aus getInputClassName
                    }
                  >
                    <input
                      type="tel"
                      id="telefon"
                      placeholder="0660 123 4567"
                      value={telefon}
                      onChange={handleChange(setTelefon)}
                      onBlur={() => handleBlur("telefon")}
                      className={getInputClassName("telefon")}
                    />
                  </ErrorTooltip>
                </div>

                <div className="input-group">
                  <label htmlFor="email">Email Addresse*</label>

                  <ErrorTooltip
                    message="Bitte gib eine gültige Email ein"
                    visible={
                      isBlurred.email &&
                      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)
                    }
                  >
                    <input
                      type="email"
                      id="email"
                      value={email}
                      placeholder="max@example.com"
                      onChange={handleChange(setEmail)}
                      onBlur={() => handleBlur("email")}
                      className={getInputClassName("email")}
                    />
                  </ErrorTooltip>
                </div>
                <div className="input-group">
                  <label htmlFor="password">Passwort*</label>

                  <ErrorTooltip
                    message="Mind. 7 Zeichen, mit Buchstaben & Zahlen."
                    visible={
                      isBlurred.password &&
                      (!password || !isStrongPassword(password))
                    }
                  >
                    <input
                      type="password"
                      id="password"
                      value={password}
                      placeholder="••••••••"
                      onChange={handleChange(setPassword)}
                      onBlur={() => handleBlur("password")}
                      className={getInputClassName("password")}
                    />
                  </ErrorTooltip>
                </div>
              </div>
              <Button onClick={(e) => stepOneFunction(e)}>
                Nächster Schritt
              </Button>
            </form>
            <div className="Register-agb-wrapper">
              <p>
                Mit der Registrierung bestätigst du unsere{" "}
                <Link href="/agb"> AGB </Link> und{" "}
                <Link href="/agb"> Datenschutzbestimmungen</Link>
              </p>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="RegistrierenMain-center-wrapper">
            <div className="Register-steps-wrapper">
              <div className="Register-steps-line"></div>
              <div className="Register-step-outer active">
                <Image
                  src={checkIcon}
                  className="Register-step-icon"
                  alt="Checkmark"
                />
              </div>
              <div className="Register-step-outer active">
                <span>2</span>
              </div>
              <div className="Register-step-outer">
                <span>3</span>
              </div>
            </div>
            <div className="Registrieren-main-title">
              <Image
                src={companyIcon}
                className="Registrieren-main-title-icon"
                alt="Company Icon"
              />
              <h3>Händlerinformationen</h3>
            </div>
            <form>
              <div className="RegistrierenMain-inputs-wrapper">
                <div className="input-group">
                  <label htmlFor="firmenname">Firmenname*</label>

                  <ErrorTooltip
                    message="Bitte gib deinen Firmennamen ein"
                    visible={isBlurred.firmenname && firmenname.length === 0}
                  >
                    <input
                      type="text"
                      id="firmenname"
                      placeholder="Auto Muster GmbH"
                      value={firmenname}
                      onChange={handleChange(setFirmenname)}
                      onBlur={() => handleBlur("firmenname")}
                      className={getInputClassName("firmenname")}
                    />
                  </ErrorTooltip>
                </div>
                <div className="input-group">
                  <label htmlFor="companyCountry">Firmensitz*</label>

                  <ErrorTooltip
                    message="Bitte wähl deinen Firmensitz aus"
                    visible={isBlurred.companyCountry && !companyCountry}
                  >
                    <select
                      id="companyCountry"
                      value={companyCountry}
                      className={getInputClassName("companyCountry")}
                      onChange={(e) => setCompanyCountry(e.target.value)}
                      onBlur={() => handleBlur("companyCountry")}
                    >
                      <option value="" disabled>
                        Bitte auswählen...
                      </option>
                      {companyCountries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </ErrorTooltip>
                </div>
                <div className="input-group">
                  <label>Firmenadresse*</label>

                  <ErrorTooltip
                    message="Bitte gib deine Straße und Hausnummer ein"
                    visible={
                      isBlurred.companyStreet && companyStreet.length === 0
                    }
                  >
                    <input
                      type="text"
                      id="companyStreet"
                      value={companyStreet}
                      onChange={handleChange(setCompanyStreet)}
                      onBlur={() => handleBlur("companyStreet")}
                      className={getInputClassName("companyStreet")}
                      placeholder="Straße & Hausnummer"
                    />
                  </ErrorTooltip>

                  <input
                    type="text"
                    id="companyAdditionalAddress"
                    value={companyAdditionalAddress}
                    onChange={handleChange(setCompanyAdditionalAddress)}
                    onBlur={() => handleBlur("companyAdditionalAddress")}
                    className={getInputClassName("companyAdditionalAddress")}
                    placeholder="Adresszusatz (optional)"
                  />

                  <ErrorTooltip
                    message="Bitte gib deine PLZ ein"
                    visible={isBlurred.postalCode && postalCode.length === 0}
                  >
                    <input
                      type="text"
                      id="postalCode"
                      value={postalCode}
                      onChange={handleChange(setPostalCode)}
                      onBlur={() => handleBlur("postalCode")}
                      className={getInputClassName("postalCode")}
                      placeholder="Postleitzahl"
                    />
                  </ErrorTooltip>

                  <ErrorTooltip
                    message="Bitte gib deine Stadt ein"
                    visible={isBlurred.city && city.length === 0}
                  >
                    <input
                      type="text"
                      id="city"
                      value={city}
                      onChange={handleChange(setCity)}
                      onBlur={() => handleBlur("city")}
                      className={getInputClassName("city")}
                      placeholder="Stadt"
                    />
                  </ErrorTooltip>

                  <ErrorTooltip
                    message="Bitte gib dein Bundesland oder deine Region ein"
                    visible={isBlurred.companyState && companyState.length === 0}
                  >
                    <input
                      type="text"
                      id="companyState"
                      value={companyState}
                      onChange={handleChange(setCompanyState)}
                      onBlur={() => handleBlur("companyState")}
                      className={getInputClassName("companyState")}
                      placeholder="Bundesland / Region"
                    />
                  </ErrorTooltip>
                </div>
                <div className="input-group">
                  <label>
                    Firmenwebsite{" "}
                    <span className="register-optional">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    id="website"
                    value={website}
                    onChange={handleChange(setWebsite)}
                    onBlur={() => handleBlur("website")}
                    placeholder="Website oder Händlerprofil (z. B. Autoscout24)"
                  />
                </div>
              </div>
              <Button onClick={(e) => stepTwoFunction(e)}>
                Nächster Schritt
              </Button>
            </form>
          </div>
        )}

        {step === 3 && (
          <div className="RegistrierenMain-center-wrapper">
            <div className="Register-steps-wrapper">
              <div className="Register-steps-line"></div>
              <div className="Register-step-outer active">
                <Image
                  src={checkIcon}
                  className="Register-step-icon"
                  alt="Checkmark"
                />
              </div>
              <div className="Register-step-outer active">
                <Image
                  src={checkIcon}
                  className="Register-step-icon"
                  alt="Checkmark"
                />
              </div>
              <div className="Register-step-outer active">
                <span>3</span>
              </div>
            </div>
            <div className="Registrieren-main-title">
              <Image
                src={preferencesIcon}
                className="Registrieren-main-title-icon"
                alt="Präferenzen"
              />
              <h3>Präferenzen beim Fahrzeugkauf</h3>
            </div>
            <div className="RegistrierenMain-inputs-wrapper">
              <div className="input-group">
                <label htmlFor="vehicleBrand">
                  Welche Fahrzeugmarken kaufst du?{" "}
                  <span className="register-optional">(Optional)</span>
                </label>
                <div className="input-group-button-wrapper">
                  <button
                    className={`input-group-button ${
                      vehicleBrand === "all" ? "active" : ""
                    }`}
                    onClick={() => setVehicleBrand("all")}
                  >
                    Alle Marken
                  </button>
                  <button
                    className={`input-group-button ${
                      vehicleBrand === "specific" ? "active" : ""
                    }`}
                    onClick={() => setVehicleBrand("specific")}
                  >
                    Spezifische Marken
                  </button>
                </div>
              </div>
              {vehicleBrand === "specific" && (
                <div className="input-group">
                  <label htmlFor="vehicleBrand">
                    Welche Fahrzeugmarken kaufst du?{" "}
                    <span className="register-optional">(Optional)</span>
                  </label>
                  <div
                    className="input-group-custom-select-wrapper"
                    ref={dropdownRef}
                  >
                    <button
                      className="input-group-select-button"
                      onClick={() => setIsVehicleDropdownOpen((prev) => !prev)}
                    >
                      Marken auswählen
                    </button>
                    {isVehicleDropdownOpen && (
                      <div className="custom-select">
                        {carBrands.map((brand) => (
                          <div
                            key={brand}
                            className={`custom-select-option ${
                              selectedBrands.includes(brand) ? "active" : ""
                            }`}
                            onClick={() => handleBrandSelection(brand)}
                          >
                            <span>{brand}</span>
                            <div
                              className={`custom-select-checkmark-wrapper ${
                                selectedBrands.includes(brand) ? "active" : ""
                              }`}
                            >
                              {selectedBrands.includes(brand) && (
                                <Image
                                  src={checkIcon}
                                  className="custom-select-checkmark"
                                  alt="checkmark"
                                />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="input-group">
                <label>
                  Wie hoch ist der durchschnittliche Einkaufswert der von dir
                  gekauften Fahrzeuge?{" "}
                  <span className="register-optional">(Optional)</span>
                </label>
                <div className="Select-wrapper">
                  <select
                    id="buyPriceMin"
                    value={buyPriceMin}
                    onChange={handleDropdownChange(setBuyPriceMin)}
                  >
                    <option value="" disabled>
                      Von
                    </option>
                    {minBuyPriceValues.map((price) => (
                      <option key={price} value={price}>
                        {price} €
                      </option>
                    ))}
                  </select>
                  <span className="select-divider">-</span>
                  <select
                    id="buyPriceMax"
                    value={buyPriceMax}
                    onChange={handleDropdownChange(setBuyPriceMax)}
                  >
                    <option value="" disabled>
                      Bis
                    </option>
                    {maxBuyPriceValues.map((price) => (
                      <option key={price} value={price}>
                        {price} €
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="input-group">
                <label>
                  Wie alt sind die Fahrzeuge, die du normalerweise kaufst (in
                  Jahren)? <span className="register-optional">(Optional)</span>
                </label>
                <div className="Select-wrapper">
                  <select
                    id="buyAgeMin"
                    value={buyAgeMin}
                    onChange={handleDropdownChange(setBuyAgeMin)}
                  >
                    <option value="" disabled>
                      Von
                    </option>
                    {buyAgeMinValues.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                  <span className="select-divider">-</span>
                  <select
                    id="buyAgeMax"
                    value={buyAgeMax}
                    onChange={handleDropdownChange(setBuyAgeMax)}
                  >
                    <option value="" disabled>
                      Bis
                    </option>
                    {buyAgeMaxValues.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="input-group">
                <label>
                  Wie hoch ist die durchschnittliche Laufleistung der von dir
                  gekauften Fahrzeuge?{" "}
                  <span className="register-optional">(Optional)</span>
                </label>
                <div className="Select-wrapper">
                  <select
                    id="buyKmMin"
                    value={buyKmMin}
                    onChange={handleDropdownChange(setBuyKmMin)}
                  >
                    <option value="" disabled>
                      Von
                    </option>
                    {buyKmMinValues.map((km) => (
                      <option key={km} value={km}>
                        {km} km
                      </option>
                    ))}
                  </select>
                  <span className="select-divider">-</span>
                  <select
                    id="buyKmMax"
                    value={buyKmMax}
                    onChange={handleDropdownChange(setBuyKmMax)}
                  >
                    <option value="" disabled>
                      Bis
                    </option>
                    {buyKmMaxValues.map((km) => (
                      <option key={km} value={km}>
                        {km} km
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="input-group">
                <label htmlFor="averageCarsPerMonth">
                  Wie viele Autos kauft dein Unternehmen durchschnittlich pro
                  Monat?
                </label>
                <select
                  id="averageCarsPerMonth"
                  value={averageCarsPerMonth}
                  onChange={handleDropdownChange(setAverageCarsPerMonth)}
                  className={getInputClassName("averageCarsPerMonth")}
                >
                  <option value="Keine Angabe">Keine Angabe</option>
                  <option value="Bis zu 5">Bis zu 5</option>
                  <option value="Bis zu 10">Bis zu 10</option>
                  <option value="Bis zu 50">Bis zu 50</option>
                  <option value="Bis zu 100">Bis zu 100</option>
                  <option value="Über 100">Über 100</option>
                </select>
              </div>
            </div>
            <Button
              onClick={() => handleSubmit()}
              isDisabled={stepThreeDisabled}
            >
              Registrieren
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default RegistrierenMain;
